import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LinkButton } from '@atlaskit/button/new';
import { selectCountryByName } from '../duck/selectors';
import { BillingDetailsFormSection } from './MACBillingDetailsFormSection';
import { State } from 'model/State';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import { CCFormWrapper, PageH3Header } from './styledComponents';
import { FormattedMessage } from 'react-intl';
import Button from '@atlaskit/button';
import { postCCDetails } from '../duck/actions/accountId';
import { useStripeOnSubmitMAC } from 'util/mac/stripeUtil';
import { fetchValidateCountryChange } from '../duck/actions/contact';
import { ErrorMessage, Form } from 'modules/stripe-credit-card';
import SectionMessage from '@atlaskit/section-message';
import Info from '@atlaskit/icon/glyph/info';
import { colors } from '@atlaskit/theme';
import config from 'appconfig';

type SelectOption = {
  label: string;
  value: string;
};

const InfoIcon = () => <Info label="Info icon" primaryColor={colors.B500} />;

export const UpdatePaymentDetailsForm = ({
  accountId,
  endDate,
  shouldShowPaymentDetailsForm,
  countryDetails,
  allCountries,
  states,
  billingDetailsForm,
  customerDetailsForContact,
  contactEmail,
  setBillingDetailsForm,
  setIsCountrySelectChanged,
  selectValidateCountry,
}) => {
  const dispatch = useDispatch();

  const [isRuBy, setIsRuBy] = useState(true);
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(0);
  const [fieldLengths, setFieldLengths] = useState({
    address1: billingDetailsForm.address1.length,
    address2: billingDetailsForm.address2.length,
    city: billingDetailsForm.city.length,
    postcode: billingDetailsForm.postcode.length,
  });
  const [currencyChangeCountry, setCurrencyChangeCountry] = useState(false);

  const currentAccountCountryDetails = useSelector((state: State) =>
    selectCountryByName(state, customerDetailsForContact?.organisationDetails.country),
  );

  useEffect(() => {
    const isISORestricted = [
      currentAccountCountryDetails,
      countryDetails?.countryIsoCode,
    ].some((item) => RESTRICTED_COUNTRIES_ISO_CODES.includes(item));

    const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(contactEmail));

    if (isISORestricted || isEmailRestricted) {
      setIsRuBy(true);
    } else {
      setIsRuBy(false);
    }
  }, [currentAccountCountryDetails, countryDetails, contactEmail]);

  useEffect(() => {
    if (allCountries?.length) {
      setCountryOptions(
        allCountries.map(({ countryIsoCode, displayName }) => ({
          label: displayName,
          value: countryIsoCode,
        })),
      );
    }
  }, [allCountries?.length]);

  const handleFormChange = (e, field) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      [field]: e.currentTarget.value,
    });
    setFieldLengths({
      ...fieldLengths,
      [field]: e.currentTarget.value.length,
    });
  };

  const handleFormCountrySelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      country: e,
      state: { label: '', value: '' },
      vatResponsible: null,
    });
    setIsCountrySelectChanged(true);
    dispatch(fetchValidateCountryChange(e.label));
  };

  const handleFormStateSelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      state: e,
      taxId: '',
    });
  };

  const handleBillingDetailsFormSubmit = () => {
    dispatch(postCCDetails(accountId));
    setIsSubmitted(isSubmitted + 1);
  };

  useEffect(() => {
    if (selectValidateCountry?.irreconcilable.length) {
      setCurrencyChangeCountry(true);
    } else {
      setCurrencyChangeCountry(false);
    }
  }, [selectValidateCountry?.irreconcilable.length]);

  return (
    <form
      onSubmit={useStripeOnSubmitMAC(() => {
        if (isRuBy) return;
        handleBillingDetailsFormSubmit();
      })}
      data-cy="billing-detail-form"
    >
      {shouldShowPaymentDetailsForm && (
        <BillingDetailsFormSection
          countryDetails={countryDetails}
          billingDetailsForm={billingDetailsForm}
          fieldLengths={fieldLengths}
          states={states}
          countryOptions={countryOptions}
          currencyChangeCountry={currencyChangeCountry}
          handleFormChange={handleFormChange}
          handleFormStateSelectChange={handleFormStateSelectChange}
          handleFormCountrySelectChange={handleFormCountrySelectChange}
          setBillingDetailsForm={setBillingDetailsForm}
        />
      )}
      <PageH3Header>
        <FormattedMessage
          defaultMessage="Credit card details"
          id="mac.payment-details.cc-details.header"
        />
      </PageH3Header>
      <p>
        <FormattedMessage
          defaultMessage="This credit card will be used for your recurring payments"
          id="mac.payment-details.cc-details.description"
        />
      </p>
      <br />
      <CCFormWrapper>
        <Form autoFocus={false} frameEmbeddingMitigation={false} />
        <ErrorMessage />
      </CCFormWrapper>
      {endDate && endDate < new Date() && (
        <p>
          <SectionMessage appearance="information" icon={InfoIcon}>
            <FormattedMessage
              defaultMessage="As your subscription has expired, your credit card will be processed shortly for the next renewal. Atlassian will also use these credit card details to automatically renew your subscription going forward."
              id="mac.payment-details.expired-subscription-message"
            />
          </SectionMessage>
        </p>
      )}
      <p>
        <FormattedMessage
          defaultMessage="By clicking here you accept the {atlCustomerAgreementLink}."
          id="mac.payment-details.customer-agreement"
          values={{
            atlCustomerAgreementLink: (
              <a
                data-link="customer-agreement"
                href={config.legalCustomerAgreement}
                target="_blank"
                title="Atlassian Customer Agreement"
                rel="noreferrer"
              >
                <FormattedMessage
                  defaultMessage="Atlassian Customer Agreement"
                  id="mac.update-cc-page.agreement.customer-agreement-link"
                />
              </a>
            ),
          }}
        />
      </p>
      <br />
      <Button
        data-cy="update-payment-details-submit-button"
        appearance="primary"
        type="submit"
        isDisabled={
          !customerDetailsForContact ||
          isRuBy ||
          !!isSubmitted ||
          currencyChangeCountry ||
          !billingDetailsForm.firstName ||
          !billingDetailsForm.lastName ||
          !billingDetailsForm.companyName ||
          !billingDetailsForm.address1 ||
          !billingDetailsForm.city ||
          !billingDetailsForm.state.value ||
          !billingDetailsForm.country.value
        }
      >
        <FormattedMessage defaultMessage="Update" id="mac.payment-details.button.update" />
      </Button>{' '}
      <LinkButton data-cy="update-payment-details-cancel-button" href="/purchase/my">
        <FormattedMessage defaultMessage="Cancel" id="mac.update-cc-page.button.cancel" />
      </LinkButton>
    </form>
  );
};
